import { Grid, Tab, Typography } from '@material-ui/core';
import { CustomTabs } from '@ncr-retail/base-retail-components';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import { handleVoucherBarcodeCommand } from 'src/barcode/business-logic/handle-voucher-barcode-command';
import BaseButton from 'src/common/components/base-button';
import DynamicScroller from 'src/common/components/dynamic-scroller';
import showPinpadDisconnectedModal from 'src/common/components/pinpad-disconnected-modal';
import { getIsManualWeightDisabled } from 'src/common/device/scale/scale-helper';
import { TenderType } from 'src/common/enumerations/tender-type';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { LocalStorage } from 'src/common/helpers/local-storage';
import { splitCamel } from 'src/common/helpers/string-helper';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { CashierPreferences } from 'src/common/interfaces/dtos/CashierPreferences';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { ITenderGroup } from 'src/common/interfaces/dtos/ITenderGroup';
import { ReactComponent as Calculator } from 'src/common/media/Calculator.svg';
import Transactions from 'src/common/media/transactions';
import { AppState, store } from 'src/common/redux/configure-store';
import { MiddleAreaActions } from 'src/common/redux/reducers/middle-area-reducer';
import { SalesKeypadActions } from 'src/common/redux/reducers/sales-keypad-reducer';
import { TenderMenuActions } from 'src/common/redux/reducers/tender-menu-reducer';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import TrainingModeWatermark from 'src/common/training-mode/training-mode-watermark';
import { getCardData } from 'src/devices/payment/get-card-details';
import { endPaymentSession } from 'src/devices/payment/pinpad-operations';
import { useResponsiveContext } from 'src/home-page/hooks/responsive-context';
import { themeColor } from 'src/mui-theme';
import { Pagination } from 'src/pagination/pagination';
import SalesKeypad from 'src/sales-keypad/components/sales-keypad';
import { findMatchingTenderInStore } from 'src/tendering/business-logic/handle-tender-payment';
import handleTenderSelection from 'src/tendering/business-logic/handle-tender-selection';
import { processTenderAmountEntryOption } from '../business-logic/process-tender-amount-entry-option';
import { exitTenderingMode } from '../business-logic/tendering-actions';
import TenderGroupTenderButtons from './tender-group-tender-buttons';
import { styles } from './tender-group-view-styles';

type Props = TenderGroupViewStates & TenderGroupViewProps & typeof mapDispatchesToProps;

const TenderGroupView = ({
  amountDue,
  total,
  isKeypadVisible,
  setKeypadVisibility,
  tenderGroup,
  setSelectedTender,
  setSelectedTenderGroup,
  trainingModeActive,
  cashierPreferences,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();
  const [disableButtons, setDisableButtons] = useState<boolean>(false);

  const scale = useSelector((state: AppState) => state.scale);

  const hasCardData = useSelector((state: AppState) => state.pinPad.hasCardData);
  const isLeftHanded = cashierPreferences.leftHanded;
  const isPinpadConfigured = useSelector((state: AppState) => state.pinPad.isPinpadConfigured);

  const { horizontalBtnAlignment } = useResponsiveContext();

  const toggleKeypad = useCallback((): void => {
    LocalStorage.setUserPreferences({ tenderingViewKeypadVisible: !isKeypadVisible });
    setKeypadVisibility(!isKeypadVisible);
  }, [isKeypadVisible, setKeypadVisibility]);

  const handleOnKeypadInputChange = (input: string): void => {
    const keypadState = store.getState().salesKeypad;
    store.dispatch(SalesKeypadActions.setSalesKeypadState({ ...keypadState, itemCode: input }));
  };
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();

  const isReturnModeActive = useSelector(
    (state: AppState): boolean => !!state.posMode.returnMode.returnType,
  );

  const handleTenderClick = async (selectedTender: ITender): Promise<void> => {
    if (!!selectedTender.isProcessedByEps && !isPinpadConfigured) {
      showPinpadDisconnectedModal();
      return;
    }

    setDisableButtons(true);
    // if we have card data but they select a different EFT tender
    if (hasCardData) {
      if (
        selectedTender.id !== findMatchingTenderInStore(getCardData())?.id &&
        selectedTender.isProcessedByEps
      ) {
        await endPaymentSession();
      }
    }
    setSelectedTender(selectedTender);
    const span = createSpan(`${selectedTender.description} Tender Selected`);

    if (selectedTender.type === TenderType.Voucher) {
      handleVoucherBarcodeCommand();
    } else {
      await handleTenderSelection(selectedTender);
      processTenderAmountEntryOption(selectedTender, amountDue);
    }

    setDisableButtons(false);
    endSpan(span);
  };

  useEffect(() => {
    setSelectedTenderGroup(tenderGroup.name);
  });

  const handleLeftHandDirection = isLeftHanded ? 'row-reverse' : 'row';

  return (
    <Grid
      container
      className={`fullHeight ${classes.tenderingContainer} ${
        trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
      }`}
      data-testid='tendering-view-container'
      direction='column'
    >
      {trainingModeActive && (
        <TrainingModeWatermark dataTestId='tendering-training-mode-watermark' />
      )}
      <DynamicScroller>
        <Grid container className='fullHeight' direction='column'>
          {horizontalBtnAlignment && (
            <div className={`fullWidth`}>
              <CustomTabs
                value={false}
                data-testid='tender-buttons'
                className={`${classes.customTabs} ${
                  trainingModeActive ? trainingModeClasses.disableBoxShadow : ''
                }`}
                spreadOut={true}
                newStyleArrows={true}
                classes={{
                  flexContainer: classes.allTabsContainer,
                  scrollButtons: classes.scrollButton,
                }}
              >
                {tenderGroup.tenders
                  .filter(t => t.isActive)
                  .map(tender => {
                    const testId = `tender-button-${tender.description}`;
                    const buttonLabel = splitCamel(tender.description)?.replace(/_/g, '');
                    let buttonClasses = classes.secondLevelTab;

                    if (hasCardData) {
                      const swipedTender = findMatchingTenderInStore(getCardData());
                      if (tender.id === swipedTender?.id) {
                        buttonClasses += ` ${classes.preSwipedTender}`;
                      }
                    }

                    return (
                      <Tab
                        key={tender.id}
                        label={buttonLabel}
                        className={buttonClasses}
                        centerRipple={true}
                        onClick={() => handleTenderClick(tender)}
                        data-testid={testId}
                        disabled={disableButtons}
                      />
                    );
                  })}
              </CustomTabs>
            </div>
          )}
          <Grid
            container
            direction={handleLeftHandDirection}
            className={`${horizontalBtnAlignment ? classes.flexSmallForm : 'fullHeight'}`}
          >
            <Grid
              item
              container
              direction='column'
              className={`fullHeight ${classes.contentContainer}`}
            >
              <Grid
                container
                justifyContent='space-between'
                className='fullHeight'
                data-testid='tendering-view-actions-container'
              >
                {!horizontalBtnAlignment && (
                  <Grid
                    item
                    className={`fullHeight ${classes.actionButtons}`}
                    style={{ flexBasis: 'auto' }}
                  >
                    <Grid
                      container
                      className='fullHeight'
                      direction='column'
                      data-testid='tender-buttons-container'
                    >
                      <Grid item className={classes.tenderButtons}>
                        <div ref={scrollRef} className={'fullHeight verticalScroll'}>
                          <TenderGroupTenderButtons
                            tenderGroup={tenderGroup}
                            handleTenderClick={handleTenderClick}
                            disabled={disableButtons}
                            horizontalBtnAlignment={horizontalBtnAlignment}
                          />
                        </div>
                      </Grid>

                      <Grid container className={classes.navButtons} justifyContent='space-evenly'>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={lastPage}
                          updateCurrentPageHandler={onPageChanged}
                          displayText={false}
                          testIdPrefix={'tender-group-view'}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid
                  item
                  container
                  className={`fullHeight ${classes.addLeftPadding} ${classes.middleContainer}`}
                >
                  <Grid container direction='column'>
                    <Grid
                      item
                      container
                      className={`fullHeight ${classes.flexStart} ${classes.positionRelative}`}
                      direction='column'
                      justifyContent='space-between'
                    >
                      <Grid item container>
                        <Grid
                          item
                          className={`${classes.total} ${classes.textAlignCenter}`}
                          data-testid='tender-group-total'
                        >
                          {`${convertCurrency(total)}`}
                        </Grid>
                        <Grid item className={`${classes.totalLabel} ${classes.textAlignCenter}`}>
                          {t('TenderingView_BalanceDue')}
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        container
                        direction='column'
                        className={`${classes.textAlignCenter}`}
                        alignItems='center'
                      >
                        <TenderPrompt
                          text={t('TenderingView_SelectTender')}
                          icon={<Transactions fill={themeColor.secondaryMain} />}
                        />
                      </Grid>
                      <Grid container className={classes.paddingTop} justifyContent='space-between'>
                        <Grid item className={classes.exit}>
                          <BaseButton
                            buttonType='primary'
                            className={classes.exitButton}
                            onClick={async () => {
                              await exitTenderingMode();
                            }}
                            data-testid='tendering-view-exit'
                          >
                            {t('TenderingView_Exit')}
                          </BaseButton>
                        </Grid>
                        {!isKeypadVisible && (
                          <Grid
                            item
                            className={isLeftHanded ? classes.keypadToggleButtonLeftHand : ''}
                          >
                            <Grid container justifyContent='center' alignContent='center'>
                              <BaseButton
                                buttonType='primary'
                                onClick={toggleKeypad}
                                data-testid='keypad-opener'
                                isSquare
                              >
                                <Calculator />
                              </BaseButton>
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {isKeypadVisible && (
              <Grid item className={`fullHeight ${classes.keypadGridItem}`}>
                <SalesKeypad
                  keyboardId={`tendering-${tenderGroup.name}`}
                  isKeypadVisible={isKeypadVisible}
                  toggleKeypad={toggleKeypad}
                  dataTestIdModifier={`tendering-${tenderGroup.name}`}
                  disableManualWeight={getIsManualWeightDisabled(isReturnModeActive, scale)}
                  disableQuantity={false}
                  handleOnInputChange={handleOnKeypadInputChange}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </DynamicScroller>
    </Grid>
  );
};

interface TenderGroupViewProps {
  tenderGroup: ITenderGroup;
}

interface TenderGroupViewStates {
  total: number;
  isKeypadVisible: boolean;
  amountDue: number;
  trainingModeActive: boolean;
  selectedTender?: ITender;
  cashierPreferences: CashierPreferences;
}

const mapStateToProps = (state: AppState): TenderGroupViewStates => ({
  total: state.itemList.total,
  isKeypadVisible: state.middleArea.salesKeypad,
  amountDue: state.itemList.amountDue,
  trainingModeActive: state.posMode.trainingMode,
  selectedTender: state.tenderMenu.selectedTender,
  cashierPreferences: state.cashierPreferences,
});

const mapDispatchesToProps = {
  setKeypadVisibility: MiddleAreaActions.setKeypadVisibility,
  setSelectedTender: TenderMenuActions.setSelectedTender,
  setSelectedTenderGroup: TenderMenuActions.setSelectedTenderGroup,
};

export default connect(mapStateToProps, mapDispatchesToProps)(TenderGroupView);

interface TenderPromptProps {
  text: string;
  icon: JSX.Element;
}

const TenderPrompt = ({ text, icon }: TenderPromptProps): JSX.Element => {
  const classes = styles();

  return (
    <>
      <Grid item className={`${classes.middleLabel} ${classes.textAlignCenter}`}>
        <Typography variant='h4' className={classes.promptText}>
          {text}
        </Typography>
      </Grid>
      <Grid item className={classes.middleIcon}>
        {icon}
      </Grid>
    </>
  );
};
